import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import axiosInstance from "../../utils/axios/axiosConfig";
import { toast } from "react-toastify";
import { DeleteModal } from "../../components";
import { useAccessChecker } from "../../utils/Functions/Authorization";
import { MagnifyingGlass, UploadSimple, Images, Copy, Trash, X } from "@phosphor-icons/react";
import {
  PageHeader, Button, Input, Skeleton, EmptyState, Pagination,
} from "../../components/ui";

const MAX_BYTES = 15 * 1024 * 1024;

const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return "—";
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
};

const formatDate = (d) =>
  new Date(d).toLocaleDateString("fr-FR", { year: "numeric", month: "short", day: "2-digit" });

function Media() {
  const { checkAccess } = useAccessChecker();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [done, setDone] = useState(false);

  // detail / edit modal
  const [selected, setSelected] = useState(null);
  const [editMeta, setEditMeta] = useState({ name: "", alt: "", caption: "", title: "" });
  const [saving, setSaving] = useState(false);

  // delete
  const [deleteId, setDeleteId] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);

  // upload
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    getMedia();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchValue, done]);

  const getMedia = async () => {
    try {
      setLoading(true);
      const { data } = await axiosInstance.post(`/media/search?page=${currentPage}&per_page=24`, { searchValue });
      setItems(data.data || []);
      setCurrentPage(data.current_page || 1);
      setTotalPages(data.last_page || 1);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Impossible de charger la médiathèque");
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (!files.length) return;
    const oversize = files.find((f) => f.size > MAX_BYTES);
    if (oversize) return toast.warning(`« ${oversize.name} » dépasse la limite de 15 Mo`);

    setUploading(true);
    let okCount = 0;
    for (const f of files) {
      try {
        const dataUri = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(f);
        });
        await axiosInstance.post("/media/store", { image: dataUri, name: f.name });
        okCount += 1;
      } catch (error) {
        toast.error(error?.response?.data?.message || `Échec de l'upload de « ${f.name} »`);
      }
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (okCount) {
      toast.success(okCount > 1 ? `${okCount} images ajoutées` : "Image ajoutée");
      setCurrentPage(1);
      setDone((d) => !d);
    }
  };

  const openDetails = (m) => {
    setSelected(m);
    setEditMeta({ name: m.name || "", alt: m.alt || "", caption: m.caption || "", title: m.title || "" });
  };

  const saveMeta = async () => {
    if (!selected) return;
    try {
      setSaving(true);
      const { data } = await axiosInstance.put(`/media/update/${selected.id}`, editMeta);
      toast.success(data.message || "Média mis à jour");
      setSelected(null);
      setDone((d) => !d);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Échec de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  const copyUrl = (url) => {
    navigator.clipboard?.writeText(url).then(
      () => toast.success("URL copiée"),
      () => toast.error("Impossible de copier l'URL")
    );
  };

  return (
    <div className="w-full p-6 text-start">
      <PageHeader
        title="Médiathèque"
        subtitle="Toutes les images du site — réutilisables dans les articles, produits et pages."
        actions={
          <>
            <Input
              icon={MagnifyingGlass}
              placeholder="Rechercher…"
              value={searchValue}
              onChange={(e) => { setSearchValue(e.target.value); setCurrentPage(1); }}
              containerClassName="w-full sm:w-64"
            />
            {checkAccess("media", "create") && (
              <>
                <Button icon={UploadSimple} loading={uploading} onClick={() => fileInputRef.current?.click()}>
                  Uploader
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={(e) => handleUpload(e.target.files)}
                />
              </>
            )}
          </>
        }
      />

      {loading ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
          {Array.from({ length: 12 }).map((_, i) => (
            <Skeleton key={i} className="aspect-square rounded-xl" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Images}
          title="Médiathèque vide"
          description="Uploadez des images pour les réutiliser partout dans le panel."
          action={
            checkAccess("media", "create") ? (
              <Button icon={UploadSimple} onClick={() => fileInputRef.current?.click()}>
                Uploader une image
              </Button>
            ) : null
          }
        />
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
          {items.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => openDetails(m)}
              className="group relative aspect-square overflow-hidden rounded-xl border border-line bg-surface-2 text-left transition hover:border-accent/60 hover:shadow-md"
              title={m.name}
            >
              <img src={m.url} alt={m.alt || m.name} loading="lazy" className="h-full w-full object-cover" />
              <div className="absolute inset-x-0 bottom-0 translate-y-full bg-gradient-to-t from-black/75 to-black/0 px-2.5 pb-2 pt-6 transition-transform duration-200 group-hover:translate-y-0">
                <div className="truncate text-xs font-medium text-white">{m.name}</div>
                <div className="text-[10px] text-white/70">
                  {m.width && m.height ? `${m.width}×${m.height} · ` : ""}{formatSize(m.size)}
                </div>
              </div>
              {!m.alt && (
                <span className="absolute left-1.5 top-1.5 rounded-md bg-amber-500/90 px-1.5 py-0.5 text-[10px] font-medium text-white" title="Texte alternatif manquant">
                  alt ?
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      <Pagination
        className="mt-6"
        page={currentPage}
        totalPages={totalPages}
        onChange={(p) => setCurrentPage(p)}
        summary={`Page ${currentPage} sur ${totalPages}`}
      />

      {/* Details / edit modal */}
      {createPortal(
        <AnimatePresence>
          {selected ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
              onClick={() => !saving && setSelected(null)}
            >
              <motion.div
                initial={{ opacity: 0, y: 14, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.97 }}
                transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                className="grid max-h-[85vh] w-full max-w-2xl grid-cols-1 overflow-hidden rounded-2xl border border-line bg-surface shadow-xl sm:grid-cols-2"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="grid place-items-center bg-surface-2 p-4">
                  <img src={selected.url} alt={selected.alt || selected.name} className="max-h-[60vh] w-full rounded-lg object-contain" />
                </div>

                <div className="flex flex-col overflow-y-auto p-5">
                  <div className="mb-4 flex items-center justify-between">
                    <h3 className="text-base font-semibold text-ink">Détails du média</h3>
                    <button
                      type="button"
                      onClick={() => setSelected(null)}
                      className="grid h-8 w-8 place-items-center rounded-lg text-muted transition hover:bg-surface-2 hover:text-ink"
                      aria-label="Fermer"
                    >
                      <X size={16} />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <Input label="Nom" value={editMeta.name} onChange={(e) => setEditMeta((p) => ({ ...p, name: e.target.value }))} />
                    <Input
                      label="Texte alternatif (alt)"
                      value={editMeta.alt}
                      onChange={(e) => setEditMeta((p) => ({ ...p, alt: e.target.value }))}
                      placeholder="Recommandé — SEO & accessibilité"
                    />
                    <Input
                      label="Légende (facultatif)"
                      value={editMeta.caption}
                      onChange={(e) => setEditMeta((p) => ({ ...p, caption: e.target.value }))}
                    />
                    <Input
                      label="Titre (facultatif)"
                      value={editMeta.title}
                      onChange={(e) => setEditMeta((p) => ({ ...p, title: e.target.value }))}
                    />

                    <div className="rounded-xl border border-line bg-surface-2 px-3 py-2.5 text-xs text-muted">
                      <div>{selected.width && selected.height ? `${selected.width} × ${selected.height} px · ` : ""}{formatSize(selected.size)}{selected.mime ? ` · ${selected.mime}` : ""}</div>
                      <div className="mt-0.5">Ajoutée le {formatDate(selected.created_at)}</div>
                    </div>

                    <button
                      type="button"
                      onClick={() => copyUrl(selected.url)}
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-accent transition hover:underline"
                    >
                      <Copy size={14} /> Copier l'URL
                    </button>
                  </div>

                  <div className="mt-auto flex items-center justify-between gap-2 pt-5">
                    {checkAccess("media", "delete") ? (
                      <Button
                        type="button"
                        variant="outline"
                        icon={Trash}
                        onClick={() => { setDeleteId(selected.id); setSelected(null); setDeleteModal(true); }}
                        className="text-red-600 hover:bg-red-500/10"
                      >
                        Supprimer
                      </Button>
                    ) : <span />}
                    {checkAccess("media", "update") && (
                      <Button type="button" onClick={saveMeta} loading={saving}>
                        Enregistrer
                      </Button>
                    )}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          ) : null}
        </AnimatePresence>,
        document.body
      )}

      <DeleteModal
        setDeleteId={setDeleteId}
        open={deleteModal}
        setOpen={setDeleteModal}
        DeleteId={deleteId}
        Model="media"
        setDone={setDone}
        setLoding={setLoading}
        done={done}
      />
    </div>
  );
}

export default Media;
